import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Link } from 'react-admin';
import { Button } from '@mui/material';

import { RangeTypeParams, reportsHttp } from '@network';
import { querySerialize } from '@utils';
import { InitialCard } from '@components';

type ReportKey = 'moderation' | 'pre_screening' | 'doc_collection' | 'kyc_screening' | 'post_screening'
  | 'completed_file' | 'bank_revision' | 'approved' | 'in_negotiations' | 'contract_in_process' | 'signed';

interface Report {
  key: ReportKey;
  title: string;
  link: string;
  bgColor: string;
  color?: string;
}

export const CreditsStatusesReport = ({ range, type, setLoader }: RangeTypeParams) => {
  const [numbers, setNumbers] = useState<Record<string, string | number>>({});
  const cls = useStyles();

  const loadNumbers = async () => {
    setLoader && setLoader(3, true);
    try {
      const nextNumbers = await reportsHttp.creditsStatuses({ range, type });
      setNumbers(nextNumbers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader && setLoader(3, false);
    }
  };

  useEffect(() => void loadNumbers(), [range, type]);

  const reports: Report[] = [
    {
      key: 'moderation', title: 'Applying', bgColor: '#2196F3',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['moderation'] }) }),
    },
    {
      key: 'pre_screening', title: 'Pre Screening', bgColor: '#1D1DB2',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['pre_screening'] }) }),
    },
    {
      key: 'doc_collection', title: 'Doc Revision', bgColor: '#3B3BB8',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['doc_collection'] }) }),
    },
    {
      key: 'kyc_screening', title: 'KYC Screening', bgColor: '#007BFF',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['kyc_screening'] }) }),
    },
    {
      key: 'post_screening', title: 'Post Screening', bgColor: '#00BFFF',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['post_screening'] }) }),
    },
    {
      key: 'completed_file', title: 'Completed File', bgColor: '#002A77',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['completed_file'] }) }),
    },
    {
      key: 'bank_revision', title: 'Analysis', bgColor: '#009688',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['bank_revision'] }) }),
    },
    {
      key: 'approved', title: 'Approved', bgColor: '#007002',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['approved'] }) }),
    },
    {
      key: 'in_negotiations', title: 'In Negotiations', bgColor: '#2E8B57',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['in_negotiations'] }) }),
    },
    {
      key: 'contract_in_process', title: 'Contract In Progress', bgColor: '#008000',
      link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['contract_in_process'] }) }),
    },
    // {
    //   key: 'signed', title: 'Signed', bgColor: '#4CAF50',
    //   link: '/credit?' + querySerialize({ filter: JSON.stringify({ status: ['signed'] }) }),
    // },
  ];

  return (
    <InitialCard
      left="Current Pipeline"
      extraPadding
      autoHeight
      mt={22}
      rightMessage="Not affected by time filter"
      rightTooltipColor={'#042E6B'}
      rightIcon={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 -960 960 960"
          width="24px"
          fill="#F38336">
          <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 27-3 53t-10 51q-14-16-32.5-27T794-418q3-15 4.5-30.5T800-480q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93q51 0 97.5-15t85.5-42q12 17 29.5 30t37.5 20q-51 41-114.5 64T480-80Zm290-160q-21 0-35.5-14.5T720-290q0-21 14.5-35.5T770-340q21 0 35.5 14.5T820-290q0 21-14.5 35.5T770-240Zm-158-52L440-464v-216h80v184l148 148-56 56Z"/>
        </svg>
      }
    >
      <br />
      <div className={cls.row}>
        {reports.map(report => {
          return (
            <div className={cls.col} key={report.key}>
              <Link to={report.link}>
                <Button className={cls.btn}>
                  <div className={cls.box} style={{
                    backgroundColor: report.bgColor,
                    color: report.color || 'white',
                  }}>
                    <div className={cls.title}>
                      {report.title}
                    </div>
                    <div className={cls.number}>
                      {+numbers[report.key] || 0}
                    </div>
                  </div>
                </Button>
              </Link>
            </div>
          );
        })}
      </div>
    </InitialCard>
  );
};

const useStyles = makeStyles({
  row: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    margin: -10,
  },
  col: {
    width: '20%',
    padding: 10,
  },
  diffPositive: {
    color: '#4CAF50',
  },
  diffNegative: {
    color: '#F22',
  },
  btn: {
    padding: 0,
    textTransform: 'none',
    display: 'block',
    width: '100%',
  },
  box: {
    padding: '10px 2px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 8,
    border: '1px solid #042E6B',
    background: '#FFF',
  },
  title: {
    fontSize: 15,
    fontWeight: 400,
  },
  number: {
    marginTop: 5,
    fontSize: 22,
    fontWeight: 700,
  },
});
